import { useState, useEffect } from 'react'
import { useFetcher } from 'react-router'
import { StyledButton } from '#app/components/styled-button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'

export function DevSettingsPanel() {
	const fetcher = useFetcher()
	const [isOpen, setIsOpen] = useState(false)
	const [currentTier, setCurrentTier] = useState<string>('free')

	useEffect(() => {
		if (process.env.NODE_ENV !== 'development') {
			return
		}
		// Load current tier on mount
		fetch('/dev/subscription-tier')
			.then((res) => res.text())
			.then((tier) => setCurrentTier(tier.trim()))
			.catch(() => {})
	}, [])

	useEffect(() => {
		if (process.env.NODE_ENV !== 'development') {
			return
		}
		// Update local state when fetcher completes
		if (fetcher.state === 'idle' && fetcher.data?.tier) {
			setCurrentTier(fetcher.data.tier)
			// Reload the page to ensure all components pick up the new tier
			window.location.reload()
		}
	}, [fetcher.state, fetcher.data])

	const handleTierChange = (tier: 'free' | 'pro') => {
		void fetcher.submit(
			{ tier },
			{ method: 'POST', action: '/dev/subscription-tier' },
		)
	}
	// Only show in development
	if (process.env.NODE_ENV !== 'development') {
		return null
	}

	return (
		<div className="fixed right-20 bottom-4 z-50">
			{!isOpen ? (
				<StyledButton
					onClick={() => setIsOpen(true)}
					size="icon"
					variant="outline"
					className="rounded-full border-yellow-500/50 bg-yellow-50/90 shadow-lg backdrop-blur-sm hover:bg-yellow-100/90 dark:border-yellow-600/50 dark:bg-yellow-950/90 dark:hover:bg-yellow-900/90"
				>
					<Icon
						name="settings"
						className="h-4 w-4 text-yellow-700 dark:text-yellow-300"
					/>
				</StyledButton>
			) : (
				<div className="flex flex-col gap-2 rounded-lg border border-yellow-500/50 bg-yellow-50/90 p-3 shadow-lg backdrop-blur-sm dark:border-yellow-600/50 dark:bg-yellow-950/90">
					<div className="flex items-center justify-between gap-4">
						<span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
							Dev Settings
						</span>
						<StyledButton
							onClick={() => setIsOpen(false)}
							size="icon"
							variant="ghost"
							className="h-6 w-6"
						>
							<Icon name="x" className="h-3 w-3" />
						</StyledButton>
					</div>

					<div className="flex flex-col gap-1">
						<span className="text-xs text-yellow-600 dark:text-yellow-400">
							Subscription Tier:
						</span>
						<div className="flex gap-1">
							<StyledButton
								onClick={() => handleTierChange('free')}
								size="sm"
								variant={currentTier === 'free' ? 'default' : 'outline'}
								className="text-xs"
								disabled={fetcher.state !== 'idle'}
							>
								Free
							</StyledButton>
							<StyledButton
								onClick={() => handleTierChange('pro')}
								size="sm"
								variant={currentTier === 'pro' ? 'default' : 'outline'}
								className="text-xs"
								disabled={fetcher.state !== 'idle'}
							>
								Pro
							</StyledButton>
						</div>
					</div>

					{fetcher.state !== 'idle' && (
						<span className="text-xs text-yellow-600 dark:text-yellow-400">
							Updating...
						</span>
					)}
				</div>
			)}
		</div>
	)
}
